import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Package, TrendingUp, TrendingDown, ShoppingCart, IndianRupee, Calendar, Search } from 'lucide-react';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useState } from 'react';

export default function ItemWisePurchase() {
  const [searchQuery, setSearchQuery] = useState('');

  // Mock Item-wise Purchase Data
  const purchaseItems = [
    {
      item_name: "Rice - Basmati 1kg",
      category: "Grocery",
      total_quantity: 150,
      unit: "kg",
      total_amount: 12000,
      avg_price: 80,
      last_purchase_date: "2025-11-15T08:00:00.000Z",
      purchases_count: 5
    },
    {
      item_name: "Cooking Oil - 1L",
      category: "Grocery",
      total_quantity: 80,
      unit: "liters",
      total_amount: 9600,
      avg_price: 120,
      last_purchase_date: "2025-11-14T08:00:00.000Z",
      purchases_count: 4
    },
    {
      item_name: "Sugar - 1kg",
      category: "Grocery",
      total_quantity: 100,
      unit: "kg",
      total_amount: 4500,
      avg_price: 45,
      last_purchase_date: "2025-11-13T08:00:00.000Z",
      purchases_count: 3
    },
    {
      item_name: "Tea Powder - 250g",
      category: "Beverages",
      total_quantity: 50,
      unit: "packets",
      total_amount: 8000,
      avg_price: 160,
      last_purchase_date: "2025-11-12T08:00:00.000Z",
      purchases_count: 4
    },
    {
      item_name: "Wheat Flour - 1kg",
      category: "Grocery",
      total_quantity: 200,
      unit: "kg",
      total_amount: 7000,
      avg_price: 35,
      last_purchase_date: "2025-11-11T08:00:00.000Z",
      purchases_count: 6
    },
    {
      item_name: "Biscuits - Parle G",
      category: "Snacks",
      total_quantity: 120,
      unit: "packets",
      total_amount: 2400,
      avg_price: 20,
      last_purchase_date: "2025-11-10T08:00:00.000Z",
      purchases_count: 8
    },
    {
      item_name: "Toothpaste - 100g",
      category: "Personal Care",
      total_quantity: 40,
      unit: "units",
      total_amount: 2000,
      avg_price: 50,
      last_purchase_date: "2025-11-09T08:00:00.000Z",
      purchases_count: 3
    },
    {
      item_name: "Soap - Lux 75g",
      category: "Personal Care",
      total_quantity: 60,
      unit: "units",
      total_amount: 1800,
      avg_price: 30,
      last_purchase_date: "2025-11-08T08:00:00.000Z",
      purchases_count: 4
    },
    {
      item_name: "Shampoo - 200ml",
      category: "Personal Care",
      total_quantity: 30,
      unit: "bottles",
      total_amount: 3600,
      avg_price: 120,
      last_purchase_date: "2025-11-07T08:00:00.000Z",
      purchases_count: 2
    },
    {
      item_name: "Potato Chips - 50g",
      category: "Snacks",
      total_quantity: 200,
      unit: "packets",
      total_amount: 2000,
      avg_price: 10,
      last_purchase_date: "2025-11-06T08:00:00.000Z",
      purchases_count: 10
    }
  ];

  // Calculate totals
  const totalPurchaseAmount = purchaseItems.reduce((sum, item) => sum + item.total_amount, 0);
  const totalItems = purchaseItems.length;
  const totalQuantityPurchased = purchaseItems.reduce((sum, item) => sum + item.total_quantity, 0);

  // Group by category
  const categoryWise = purchaseItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        items: 0,
        amount: 0,
        quantity: 0
      };
    }
    acc[item.category].items += 1;
    acc[item.category].amount += item.total_amount;
    acc[item.category].quantity += item.total_quantity;
    return acc;
  }, {} );

  // Filter items based on search
  const filteredItems = purchaseItems.filter(item =>
    item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-gray-900 mb-1">Item-wise Purchase</h2>
        <p className="text-gray-600">Track all items you've purchased from us</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-gray-900">
              <span>Total Purchase</span>
              <IndianRupee className="w-5 h-5 text-blue-600" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-900 mb-1">₹{totalPurchaseAmount.toLocaleString('en-IN')}</p>
            <p className="text-gray-600">{totalItems} different items</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-gray-900">
              <span>Total Items</span>
              <Package className="w-5 h-5 text-green-600" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-900 mb-1">{totalItems}</p>
            <p className="text-gray-600">Unique products</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-gray-900">
              <span>Total Quantity</span>
              <ShoppingCart className="w-5 h-5 text-purple-600" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-900 mb-1">{totalQuantityPurchased}</p>
            <p className="text-gray-600">Units purchased</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger className="data-[state=active]:bg-white data-[state=active]:text-blue-600 
                 data-[state=active]:shadow-sm data-[state=active]:rounded-l-md rounded-r-none transition-all duration-300 
                 border-0 "  value="all">All Items</TabsTrigger>
          <TabsTrigger  className="data-[state=active]:bg-white data-[state=active]:text-blue-600 
      data-[state=active]:shadow-sm transition-all duration-300 
      rounded-md border-0  data-[state=active]:rounded-r-md rounded-l-none " value="category">By Category</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search items or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Items List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Purchase Details ({filteredItems.length} items)</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Desktop View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-700">Item Name</th>
                      <th className="px-4 py-3 text-left text-gray-700">Category</th>
                      <th className="px-4 py-3 text-right text-gray-700">Quantity</th>
                      <th className="px-4 py-3 text-right text-gray-700">Avg Price</th>
                      <th className="px-4 py-3 text-right text-gray-700">Total Amount</th>
                      <th className="px-4 py-3 text-left text-gray-700">Last Purchase</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{item.item_name}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {item.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          {item.total_quantity} {item.unit}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          ₹{item.avg_price}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          ₹{item.total_amount.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(item.last_purchase_date).toLocaleDateString('en-IN', { 
                            day: '2-digit', 
                            month: 'short' 
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View */}
              <div className="md:hidden space-y-3">
                {filteredItems.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-gray-900">{item.item_name}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {item.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-900">₹{item.total_amount.toLocaleString('en-IN')}</p>
                        <p className="text-gray-600 text-xs mt-1">
                          {new Date(item.last_purchase_date).toLocaleDateString('en-IN', { 
                            day: '2-digit', 
                            month: 'short' 
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-200">
                      <div>
                        <p className="text-gray-600 text-xs">Quantity</p>
                        <p className="text-gray-900">{item.total_quantity} {item.unit}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Avg Price</p>
                        <p className="text-gray-900">₹{item.avg_price}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Orders</p>
                        <p className="text-gray-900">{item.purchases_count}x</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredItems.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No items found matching "{searchQuery}"
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="category" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Category-wise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(categoryWise).map(([category, data]) => {
                  const percentage = (data.amount / totalPurchaseAmount) * 100;
                  
                  return (
                    <div key={category} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-gray-900">{category}</p>
                          <p className="text-gray-600">{data.items} items • {data.quantity} units</p>
                        </div>
                        <p className="text-gray-900">₹{data.amount.toLocaleString('en-IN')}</p>
                      </div>
                      
                      {/* Visual Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <p className="text-gray-600 text-xs mt-1">{percentage.toFixed(1)}% of total purchases</p>
                    </div>
                  );
                })}
              </div>

              {/* Category Summary Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                {Object.entries(categoryWise).map(([category, data]) => (
                  <div key={category} className="text-center p-3 bg-white rounded-lg border">
                    <p className="text-gray-600 text-xs mb-1">{category}</p>
                    <p className="text-gray-900">₹{(data.amount / 1000).toFixed(1)}K</p>
                    <p className="text-gray-500 text-xs">{data.items} items</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Top 5 Most Purchased */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Top 5 Most Purchased Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {purchaseItems
              .sort((a, b) => b.total_amount - a.total_amount)
              .slice(0, 5)
              .map((item, index) => {
                const maxAmount = purchaseItems[0].total_amount;
                const percentage = (item.total_amount / maxAmount) * 100;
                
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white">#{index + 1}</span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-gray-900">{item.item_name}</p>
                        <p className="text-gray-900">₹{item.total_amount.toLocaleString('en-IN')}</p>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      
                      <p className="text-gray-600 text-xs mt-1">
                        {item.total_quantity} {item.unit} • {item.purchases_count} orders
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
